<script>
import * as m from '@app/paraglide/messages.js';
import { projectState } from "@app/store/projectStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { speakerState } from "@app/store/speakerStore.svelte.js";
import { isAudioGroupType } from "@app/lib/eventGroupTypes.js";
import { 
    filterState, 
    resetFilters, 
    hasActiveFilters
} from "@app/store/editorStore.svelte.js";
import { slide } from "svelte/transition";
import { onDestroy } from "svelte";
import debounce from "just-debounce-it";

// Local state for debounced text input
let textInput = $state("");

// Resizable height for the scrollable filter controls area (px)
const MIN_PANEL_HEIGHT = 80;
let panelHeight = $state(180);
let dragStartY = 0;
let dragStartHeight = 0;

function handleResize(event) {
    const delta = dragStartY - event.clientY; // dragging up grows the panel
    const maxHeight = Math.round(window.innerHeight * 0.8);
    panelHeight = Math.min(Math.max(dragStartHeight + delta, MIN_PANEL_HEIGHT), maxHeight);
}

function stopResize() {
    window.removeEventListener("pointermove", handleResize);
    window.removeEventListener("pointerup", stopResize);
}

function startResize(event) {
    dragStartY = event.clientY;
    dragStartHeight = panelHeight;
    event.preventDefault();
    window.addEventListener("pointermove", handleResize);
    window.addEventListener("pointerup", stopResize);
}

onDestroy(stopResize);

// Debounced function to update filter text
const updateFilterText = debounce((value) => {
    filterState.text = value;
}, 300);

// Watch textInput changes and debounce updates
$effect(() => {
    updateFilterText(textInput);
});

// Sync textInput with filterState when filters are reset
$effect(() => {
    if (!hasActiveFilters()) {
        textInput = "";
    }
});

// Get unique tags from all events in current event group
let availableTags = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return [];
    
    const tagSet = new Set();
    eventGroupState[projectState.selected].events.forEach(event => {
        if (event.tags && Array.isArray(event.tags)) {
            event.tags.forEach(tag => {
                const tagName = typeof tag === 'string' ? tag : tag.term;
                if (tagName) tagSet.add(tagName);
            });
        }
    });
    
    return Array.from(tagSet).sort();
});

// Handle speaker selection toggle
function toggleSpeaker(speakerId) {
    const index = filterState.speakers.indexOf(speakerId);
    if (index === -1) {
        filterState.speakers = [...filterState.speakers, speakerId];
    } else {
        filterState.speakers = filterState.speakers.filter(id => id !== speakerId);
    }
}

// Handle tag selection toggle
function toggleTag(tagName) {
    const index = filterState.tags.indexOf(tagName);
    if (index === -1) {
        filterState.tags = [...filterState.tags, tagName];
    } else {
        filterState.tags = filterState.tags.filter(t => t !== tagName);
    }
}

// Line count options (4 represents "4 or more")
const lineCountOptions = [
    { value: 1, label: "1" },
    { value: 2, label: "2" },
    { value: 3, label: "3" },
    { value: 4, label: "4+" }
];

// Handle line count selection toggle
function toggleLineCount(count) {
    const index = filterState.lineCounts.indexOf(count);
    if (index === -1) {
        filterState.lineCounts = [...filterState.lineCounts, count];
    } else {
        filterState.lineCounts = filterState.lineCounts.filter(c => c !== count);
    }
}

const letterTrackOptions = ["none", "A", "B", "C", "D"];

function letterTrackLabel(value) {
    if (value === "none") return m.event_track_none();
    return `${m.multiview_track()} ${value}`;
}

let isMultiviewGroup = $derived(
    eventGroupState[projectState.selected]?.type === "multiview"
);

let isAudioGroup = $derived(
    isAudioGroupType(eventGroupState[projectState.selected]?.type)
);

let multiviewTrackNumbers = $derived.by(() => {
    const eventGroup = eventGroupState[projectState.selected];
    if (!eventGroup || eventGroup.type !== "multiview") return [];

    let count;
    if (Array.isArray(eventGroup.tracks) && eventGroup.tracks.length >= 2) {
        count = eventGroup.tracks.length;
    } else {
        count = Math.max(
            2,
            ...eventGroup.events.map((event) => event.track || 1),
        );
    }

    return Array.from({ length: count }, (_, i) => i + 1);
});

function toggleTrack(track) {
    const index = filterState.tracks.indexOf(track);
    if (index === -1) {
        filterState.tracks = [...filterState.tracks, track];
    } else {
        filterState.tracks = filterState.tracks.filter(t => t !== track);
    }
}

// Clear all filters
function clearAllFilters() {
    resetFilters();
    textInput = "";
}
</script>

<!-- Filter Controls Panel (slides up from bottom) -->
{#if filterState.panelVisible}
    <div class="filter-panel border-top border-light-subtle bg-light text-dark" transition:slide={{ duration: 300 }}>
        <!-- Resize handle (drag to change panel height) -->
        <div
            class="resize-handle"
            role="separator"
            aria-orientation="horizontal"
            aria-label={m.editor_filter_resize_handle()}
            title={m.editor_filter_resize_handle()}
            onpointerdown={startResize}
        ></div>

        <!-- Header row with title and close -->
        <div class="d-flex align-items-center gap-2 px-2 py-1 border-bottom bg-secondary bg-opacity-25">
            <span class="fw-semibold small"><i class="bi bi-funnel-fill text-primary me-1"></i>{m.editor_filters_title()}</span>
            
            <div class="ms-auto">
                <button 
                    class="btn btn-sm btn-outline-dark border-0"
                    onclick={clearAllFilters}
                    title={m.editor_clear_all_filters()}>
                    <i class="bi bi-x-lg"></i>
                    {m.editor_clear_button()}
                </button>
            </div>
        </div>

        <!-- Filter Controls -->
        <div class="filter-controls px-2 py-2" style="max-height: {panelHeight}px;">
            <div class="row align-items-end gy-3">
                <!-- Text Search -->
                <div class="col-md-4">
                    <label for="filterSearchText" class="form-label small mb-1">{m.editor_search_text_label()}</label>
                    <input 
                        type="text"
                        id="filterSearchText" 
                        class="form-control form-control-sm"
                        placeholder={m.editor_search_placeholder()}
                        bind:value={textInput}
                    />
                </div>

                <!-- Filter Switches -->
                 <div class="col-auto">
                    <div class="form-check form-switch">
                        <input 
                            class="form-check-input" 
                            type="checkbox" 
                            role="switch"
                            id="filterHasError"
                            bind:checked={filterState.hasError}
                        />
                        <label class="form-check-label" for="filterHasError">{m.editor_filter_errors()}</label>
                </div>
                 </div>
                
                <div class="col-auto">
                    <div class="form-check form-switch">
                        <input 
                            class="form-check-input" 
                            type="checkbox" 
                            role="switch"
                            id="filterMissingTC"
                            bind:checked={filterState.hasMissingTimecode}
                        />
                        <label class="form-check-label" for="filterMissingTC">{m.editor_filter_no_tc()}</label>
                    </div>
                </div>
                <div class="col-auto">
                    <div class="form-check form-switch">
                        <input 
                            class="form-check-input" 
                            type="checkbox" 
                            role="switch"
                            id="filterForced"
                            bind:checked={filterState.hasForced}
                        />
                        <label class="form-check-label" for="filterForced">{m.editor_filter_forced()}</label>
                    </div>
                </div>
                <div class="col-auto">
                    <div class="form-check form-switch">
                        <input 
                            class="form-check-input" 
                            type="checkbox" 
                            role="switch"
                            id="filterNonForced"
                            bind:checked={filterState.hasNonForced}
                        />
                        <label class="form-check-label" for="filterNonForced">{m.editor_filter_non_forced()}</label>
                    </div>
                </div>
                <div class="col-auto">
                    <div class="form-check form-switch">
                        <input 
                            class="form-check-input" 
                            type="checkbox" 
                            role="switch"
                            id="filterFailedApproval"
                            bind:checked={filterState.hasFailedApproval}
                        />
                        <label class="form-check-label" for="filterFailedApproval">{m.editor_filter_failed()}</label>
                    </div>
                </div>
                <div class="col-auto">
                    <div class="form-check form-switch">
                        <input 
                            class="form-check-input" 
                            type="checkbox" 
                            role="switch"
                            id="filterApproved"
                            bind:checked={filterState.hasApproved}
                        />
                        <label class="form-check-label" for="filterApproved">{m.editor_filter_approved()}</label>
                    </div>
                </div>
                <div class="col-auto">
                    <div class="form-check form-switch">
                        <input 
                            class="form-check-input" 
                            type="checkbox" 
                            role="switch"
                            id="filterNeedsReview"
                            bind:checked={filterState.hasNeedsReview}
                        />
                        <label class="form-check-label" for="filterNeedsReview">{m.editor_filter_needs_review()}</label>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="form-check form-switch">
                        <input 
                            class="form-check-input" 
                            type="checkbox" 
                            role="switch"
                            id="filterIncompleteComment"
                            bind:checked={filterState.hasIncompleteComment}
                        />
                        <label class="form-check-label" for="filterIncompleteComment">{m.editor_filter_incomplete_comment()}</label>
                    </div>
                </div>
                {#if isAudioGroup}
                <div class="col-md-4">
                    <div class="form-check form-switch">
                        <input 
                            class="form-check-input" 
                            type="checkbox" 
                            role="switch"
                            id="filterRenderRequired"
                            bind:checked={filterState.hasRenderRequired}
                        />
                        <label class="form-check-label" for="filterRenderRequired">{m.editor_filter_render_required()}</label>
                    </div>
                </div>
                {/if}
            </div>

            <!-- Line Count and Track Filters -->
            <div class="row g-2 mt-1">
                <div class="col-auto">
                    <span class="form-label small mb-1 d-block">{m.editor_filter_lines_label()}</span>
                    <div class="d-flex flex-wrap gap-1" role="group" aria-label={m.editor_filter_lines_label()}>
                        {#each lineCountOptions as option (option.value)}
                            <button 
                                class="btn btn-sm {filterState.lineCounts.includes(option.value) ? 'btn-primary' : 'btn-outline-secondary'}"
                                onclick={() => toggleLineCount(option.value)}>
                                {option.label}
                            </button>
                        {/each}
                    </div>
                </div>
                <div class="col-auto">
                    <span class="form-label small mb-1 d-block">{m.editor_filter_tracks_label()}</span>
                    <div class="d-flex flex-wrap gap-1" role="group" aria-label={m.editor_track_filters_label()}>
                        {#if isMultiviewGroup}
                            {#each multiviewTrackNumbers as trackNumber (trackNumber)}
                                <button
                                    class="btn btn-sm {filterState.tracks.includes(trackNumber) ? 'btn-primary' : 'btn-outline-secondary'}"
                                    onclick={() => toggleTrack(trackNumber)}>
                                    {trackNumber}
                                </button>
                            {/each}
                        {:else}
                            {#each letterTrackOptions as trackValue (trackValue)}
                                <button
                                    class="btn btn-sm {filterState.tracks.includes(trackValue) ? 'btn-primary' : 'btn-outline-secondary'}"
                                    onclick={() => toggleTrack(trackValue)}>
                                    {letterTrackLabel(trackValue)}
                                </button>
                            {/each}
                        {/if}
                    </div>
                </div>
            </div>

            <!-- Second Row: Speakers and Tags -->
            {#if speakerState.length > 0 || availableTags.length > 0}
                <div class="row g-2 mt-1">
                    <!-- Speakers -->
                    {#if speakerState.length > 0}
                        <div class="col-md-6">
                            <span class="form-label small mb-1 d-block">{m.editor_speakers_label()}</span>
                            <div class="d-flex flex-wrap gap-1" role="group" aria-label={m.editor_speaker_filters_label()}>
                                {#each speakerState as speaker}
                                    <button 
                                        class="btn btn-sm {filterState.speakers.includes(speaker.id || speaker.name) ? 'btn-primary' : 'btn-outline-secondary'}"
                                        onclick={() => toggleSpeaker(speaker.id || speaker.name)}>
                                        {speaker.name}
                                    </button>
                                {/each}
                            </div>
                        </div>
                    {/if}

                    <!-- Tags -->
                    {#if availableTags.length > 0}
                        <div class="col-md-6">
                            <span class="form-label small mb-1 d-block">{m.editor_tags_label()}</span>
                            <div class="d-flex flex-wrap gap-1" role="group" aria-label={m.editor_tag_filters_label()}>
                                {#each availableTags as tag}
                                    <button 
                                        class="btn btn-sm {filterState.tags.includes(tag) ? 'btn-info' : 'btn-outline-secondary'}"
                                        onclick={() => toggleTag(tag)}>
                                        {tag}
                                    </button>
                                {/each}
                            </div>
                        </div>
                    {/if}
                </div>
            {/if}
        </div>
    </div>
{/if}

<style>
.filter-panel {
    font-size: 0.875rem;
    box-shadow: 6px -10px 31px 3px #000000;
}

.resize-handle {
    height: 10px;
    cursor: ns-resize;
    touch-action: none;
    background: repeating-linear-gradient(
        90deg,
        transparent,
        transparent 6px,
        rgba(108, 117, 125, 0.4) 6px,
        rgba(108, 117, 125, 0.4) 10px
    );
    background-position: center;
    background-size: 40px 4px;
    background-repeat: no-repeat;
}

.resize-handle:hover {
    background-color: rgba(13, 110, 253, 0.12);
}

.filter-controls {
    overflow-y: auto;
}

.form-label {
    font-weight: 500;
    color: #6c757d;
}
</style>
